import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbtack } from '@fortawesome/pro-solid-svg-icons';
import { styled } from 'styled-components';
import { getStatsTranslations } from 'utils/helpers';
import { ReturnLogCreatorType, ReturnLogItem } from 'returnCenter-api';
import { TFunction } from 'i18next';

export interface LogItemProps {
    log: ReturnLogItem;
    t: TFunction;
    loading: boolean;
    onTogglePin: (logId: number | undefined, pinned: boolean) => void;
}

const getCreatorTypeLabel = (creatorType: ReturnLogCreatorType | undefined, t: (key: string) => string): string => {
    switch (creatorType) {
        case ReturnLogCreatorType.System:
            return t('creatorType.system');
        case ReturnLogCreatorType.User:
            return t('creatorType.user');
        case ReturnLogCreatorType.Partner:
            return t('creatorType.partner');
        case ReturnLogCreatorType.Customer:
            return t('creatorType.customer');
        default:
            return t('unknown');
    }
};

const LogItemComponent = ({ log, t, loading, onTogglePin }: LogItemProps) => (
    <LogItemWrapper column $pinned={log.pinned}>
        {/* Header: Creator badge left, date right */}
        <LogHeader justify="between" align="center">
            <CreatorBadge $creatorType={log.creatorType}>{getCreatorTypeLabel(log.creatorType, t)}</CreatorBadge>
            <Text margin={0.5} fontSize={12} color="textFaded">
                {log.date ? new Date(log.date).toLocaleString() : '-'}
            </Text>
        </LogHeader>
        {/* Message row */}
        <Flex align="center" gap="4px" className="mb-1">
            <Label as="label">{t('log.message')}: </Label>
            <Text as="span" fontSize={11}>
                {log.comment}
            </Text>
        </Flex>
        {/* Status/user left, pin right */}
        <LogFooter justify="between" align="start">
            <Flex column align="start" gap="0.25rem">
                <Flex justify="center" align="center" gap="4px">
                    <Label as="label">Status:</Label>
                    <Text as="span" fontSize={11} bold>
                        {getStatsTranslations(t, log.type ?? undefined)}
                    </Text>
                </Flex>
                <Flex justify="center" align="center" gap="4px">
                    <Label as="label">{t('log.user')}:</Label>
                    <Text as="span" fontSize={11}>
                        {log.user ?? t('unknown')}
                    </Text>
                </Flex>
            </Flex>
            <PinButton
                onClick={() => onTogglePin(log.id, !log.pinned)}
                title={log.pinned ? t('unpin') : t('pin')}
                $pinned={log.pinned}
                disabled={loading}
            >
                <FontAwesomeIcon icon={faThumbtack} />
            </PinButton>
        </LogFooter>
    </LogItemWrapper>
);

export default LogItemComponent;

const LogItemWrapper = styled(Flex)<{ $pinned?: boolean }>`
    padding: 0 0 0.5rem 0.5rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    ${({ $pinned, theme }) =>
        $pinned &&
        `
        background-color: ${theme.colors.warning}20;
        border: 1px solid ${theme.colors.warning};
        border-left: 3px solid ${theme.colors.warning};
        border-radius: var(--radius);
        padding-top: 0.1rem;
        
    `}
`;

const Label = styled(Text)`
    font-size: 10px;
    font-weight: 600;
`;

const LogHeader = styled(Flex)`
    width: 100%;
`;

const LogFooter = styled(Flex)`
    width: 100%;
    margin-bottom: 0.25rem;
`;

const PinButton = styled.button<{ $pinned?: boolean }>`
    background: none;
    border: none;
    align-self: end;
    cursor: pointer;

    justify-content: center;
    color: ${({ theme, $pinned }) => ($pinned ? '#ef6c00' : theme.colors.main)};
    font-size: 12px;
    transition: all 0.2s ease;

    &:hover {
        background-color: ${({ theme }) => theme.colors.fill};
        color: ${({ theme }) => theme.colors.main};

        ${({ $pinned }) =>
            !$pinned &&
            `
            color: #ef6c00;
        `}
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
`;

const getCreatorBadgeColor = (creatorType: ReturnLogCreatorType | undefined) => {
    switch (creatorType) {
        case ReturnLogCreatorType.System:
            return { bg: '#e3f2fd', text: '#1565c0' };
        case ReturnLogCreatorType.User:
            return { bg: '#e8f5e9', text: '#2e7d32' };
        case ReturnLogCreatorType.Partner:
            return { bg: '#fff3e0', text: '#ef6c00' };
        case ReturnLogCreatorType.Customer:
            return { bg: '#fce4ec', text: '#c2185b' };
        default:
            return { bg: '#f5f5f5', text: '#757575' };
    }
};

const CreatorBadge = styled.span<{ $creatorType?: ReturnLogCreatorType }>`
    font-size: 10px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: var(--radius);
    background-color: ${({ $creatorType }) => getCreatorBadgeColor($creatorType).bg};
    color: ${({ $creatorType }) => getCreatorBadgeColor($creatorType).text};
`;
